// MARK://dhk8/pos/lib/clients/orders
// Typed HTTP client for dhk8-pos-orders, reconciled against the published contract
// at services/dhk8-pos-orders/src/contract.ts (commit 0ec4ee2).
//
// Routes: `${origin}/v1/orders/...` (canonical), transparent through the Edge proxy.
// Response envelope: { ok: true, data: T } | { ok: false, error: { code, message } }.
// State machine: OPEN → SENT_TO_KITCHEN → PARTIALLY_PAID → CLOSED  (cancel: → CANCELLED).
// Totals are tax-exclusive; the server recomputes on every mutating call and returns
// the full OrderDto. Persistent payment records live in dhk8-pos-payments-mixed
// (Sprint E) — POST /payments here only bumps paidCents + status.
//
// DTO types here mirror the contract. order-store.ts adapts BackendOrderDto into the
// UI-friendly OrderDto it exposes to the rest of the app (anti-corruption layer).

import { apiFetch, svcUrl } from '../api.js'
import { PREVIEW_STUB, SEED_ORDERS, makeStubOrder, recomputeStubOrder } from '../seed-preview.js'
import { gqlClient } from '../gql.js'
import { PosOrdersQuery, PosOrderQuery, PosOrderOpenByTableSessionQuery, PosOrderPaymentsQuery, PosRecordOrderPaymentMutation, PosCreateOrderMutation, PosAddOrderItemMutation, PosUpdateOrderItemMutation, PosRemoveOrderItemMutation, PosFireOrderMutation, PosApplyOrderDiscountMutation, PosSetOrderTipMutation, PosCancelOrderMutation, PosReopenOrderMutation, PosCreateOrderSubAccountMutation, PosUpdateOrderSubAccountMutation, PosMoveOrderMutation, PosRefundOrderMutation } from '../queries/orders.queries.js'

// dhk8-pos-orders is reached through the ingress route /v1/dhk8-pos-orders/* → the
// service sees /v1/orders/... So the full URL is `${apiBase()}/dhk8-pos-orders/v1/orders/...`.
const ORDERS_BASE = svcUrl('dhk8-pos-orders', '/v1/orders')

type Envelope<T> = { ok: true; data: T } | { ok: false; error: { code: string; message: string } }

async function fetchData<T>(url: string, init?: Parameters<typeof apiFetch>[1]): Promise<T> {
  const body = await apiFetch<Envelope<T> | T>(url, init)
  if (body !== null && typeof body === 'object' && 'ok' in body) {
    const env = body as Envelope<T>
    if (env.ok) return env.data
    throw new Error(`${env.error.code}: ${env.error.message}`)
  }
  return body as T
}

// ── Response DTOs (mirror of services/dhk8-pos-orders/src/contract.ts) ───────────

export interface BackendOrderItemModifierDto {
  id: string
  orderItemId: string
  modifierId: string
  name: string
  priceDeltaCents: number
}

export interface BackendOrderItemDto {
  id: string
  orderId: string
  subAccountId: string | null
  productId: string
  variantId: string | null
  variantName: string | null
  comboId: string | null
  name: string
  quantity: number
  unitPriceCents: number
  discountCents: number
  taxRate: string
  taxCents: number
  totalCents: number
  status: 'PENDING' | 'SENT' | 'PREPARING' | 'READY' | 'DELIVERED' | 'CANCELLED'
  kitchenStationId: string | null
  sentToKitchenAt: string | null
  readyAt: string | null
  deliveredAt: string | null
  notes: string | null
  parentOrderItemId: string | null
  isComboRoot: boolean
  createdAt: string
  /** Ronda (tanda) en la que se disparó; null mientras sigue en el carrito. */
  roundId?: string | null
  modifiers: BackendOrderItemModifierDto[]
}

export interface BackendOrderSubAccountDto {
  id: string
  orderId: string
  label: string
  discountCents: number
  tipCents: number
  notes: string | null
  createdAt: string
  closedAt: string | null
}

export interface BackendOrderDto {
  id: string
  tenantId: string
  branchId: string
  folio: string
  tableSessionId: string | null
  externalOrderId: string | null
  customerId: string | null
  covers: number
  type: 'DINE_IN' | 'TAKEOUT' | 'DELIVERY' | 'DRIVE_THROUGH'
  status: 'PENDING_ACCEPTANCE' | 'OPEN' | 'SENT_TO_KITCHEN' | 'PARTIALLY_PAID' | 'CLOSED' | 'CANCELLED' | 'REJECTED'
  openedByUserId: string
  closedByUserId: string | null
  /** Super-set guest — aceptación manual (null en órdenes de staff). */
  chosenPaymentMethod?: string | null
  acceptedByUserId?: string | null
  acceptedAt?: string | null
  fireHoldUntil?: string | null
  rejectedByUserId?: string | null
  rejectedAt?: string | null
  rejectReason?: string | null
  /** Código de recolección (`XXXX-99999`) que el comensal presenta al recoger.
   *  Lo genera el servidor para los pedidos que SE RECOGEN; null en mesa. */
  pickupCode?: string | null
  deliveredAt?: string | null
  deliveredByUserId?: string | null
  subtotalCents: number
  discountCents: number
  discountMeta: unknown
  taxCents: number
  tipCents: number
  totalCents: number
  paidCents: number
  notes: string | null
  openedAt: string
  closedAt: string | null
  cancelledAt: string | null
  cancelReason: string | null
  globalInvoiceId: string | null
  /** Sequential consumer-facing ticket number minted when the order is closed (distinct
   *  from `folio`, the internal order id). Null/absent while the order is still open. */
  ticketFolio?: string | null
  /** UUIDv7 público que actúa como token para descargar el ticket en
   *  `https://tickets.nintaigo.com/{uuid}`. Asignado al CLOSED (backend `recordPayment`);
   *  el `getById` del backend backfilla on-read para órdenes legacy cerradas antes del
   *  deploy de la lógica. Null/undefined para órdenes OPEN/CANCELLED. */
  publicTicketUuid?: string | null
  items: BackendOrderItemDto[]
  subAccounts: BackendOrderSubAccountDto[]
  /** Rondas (tandas) PENDIENTES de aceptación: lo que el comensal pidió sobre
   *  una cuenta ya abierta y el mesero aún no autoriza. Vacío en la mayoría. */
  pendingRounds?: BackendOrderRoundDto[]
}

export interface BackendOrderRoundDto {
  id: string
  number: number
  origin: string
  createdAt: string
}

export interface OrderListResponse {
  items: BackendOrderDto[]
  total: number
  page: number
  limit: number
}

// ── Request DTOs ────────────────────────────────────────────────────────────────

export interface CreateOrderInput {
  tenantId: string
  branchId: string
  type?: BackendOrderDto['type']
  tableSessionId?: string
  covers?: number
  openedByUserId: string
  customerId?: string
  notes?: string
}

export interface AddItemInput {
  productId: string
  name: string
  quantity: number
  unitPriceCents: number
  taxRate?: string | number
  discountCents?: number
  subAccountId?: string
  variantId?: string
  variantName?: string
  modifiers?: { modifierId: string; name: string; priceDeltaCents: number }[]
  notes?: string
}

export interface UpdateItemInput {
  quantity?: number
  discountCents?: number
  notes?: string
  status?: BackendOrderItemDto['status']
}

export interface ApplyOrderDiscountInput {
  discountCents: number
  reason: string
  appliedBy: string
  /** When set, the discount is scoped to that sub-account only (its items).
   *  TODO backend: dhk8-pos-orders has no per-sub-account discount route yet; on the
   *  real path this is sent through and ignored until the contract supports it. The
   *  preview stub honours it (writes BackendOrderSubAccountDto.discountCents). */
  subAccountId?: string | null
}

export interface RecordPaymentInput {
  amountCents: number
  method: string
  tipCents?: number
  changeCents?: number
  reference?: string
  capturedById: string
  /** When set, the payment is recorded against that sub-account (split bill); omit for a
   *  payment against the whole order. Enables partial / sub-account / mixed tenders. */
  subAccountId?: string | null
  /** Last 4 digits of the card (card tenders only) — surfaced on the receipt. */
  cardLastFour?: string
  /** Cash handed over by the customer (cash tenders) — `changeCents` derives from it. */
  cashGivenCents?: number
  /** Idempotency key — also sent as the `Idempotency-Key` header so retries are safe. */
  idempotencyKey?: string
}

/** A persisted payment record on an order (returned by GET /v1/orders/:id/payments and
 *  embedded in the refund response). Mirror of dhk8-pos-orders' OrderPaymentDto. */
export interface BackendOrderPaymentDto {
  id: string
  orderId: string
  subAccountId: string | null
  method: string
  amountCents: number
  tipCents: number
  changeCents: number
  cardLastFour: string | null
  reference: string | null
  status: 'CAPTURED' | 'VOIDED' | 'REFUNDED' | 'PARTIALLY_REFUNDED'
  refundedCents: number
  capturedById: string
  capturedAt: string
  refundedAt: string | null
}

export interface RefundPaymentInput {
  /** Amount to refund in cents; omit to refund the full captured amount. */
  amountCents?: number
  reason: string
  /** User authorising the refund (manager). */
  refundedById: string
}

export interface RefundPaymentResult {
  payment: BackendOrderPaymentDto
  order: BackendOrderDto
}

/** Order-level refund record returned by POST /v1/orders/:orderId/refund.
 *  Backed by `dhk8_pos.PosRefund` (F3, sprint sprint 2026-05-15). */
export interface BackendRefundDto {
  id: string
  tenantId: string
  branchId: string
  orderId: string
  paymentId: string | null
  amountCents: number
  reason: string
  authorizedById: string
  status: string
  cfdiCancelled: boolean
  createdAt: string
}

export interface RefundOrderInput {
  amountCents: number
  reason: string
  /** Manager whose PIN authorised the refund. */
  managerUserId: string
  /** Proof that the caller verified the PIN against dhk8-platform-auth's
   *  /v1/auth/manager-verify. Forwarded in the body; the future gateway will
   *  reject calls lacking a valid hash. */
  managerPinPasswordHash: string
  /** F9.6 idempotency key — same key on a retried/replayed refund yields a single PosRefund. */
  idempotencyKey?: string
}

export interface RefundOrderResult {
  refund: BackendRefundDto
  order: BackendOrderDto
}

export interface ListOrdersQuery {
  tenantId: string
  branchId?: string
  status?: BackendOrderDto['status']
  /** ISO 8601 — filter by openedAt >= from (for reports / cash-cut periods). */
  from?: string
  /** ISO 8601 — filter by openedAt <= to. */
  to?: string
  page?: number
  limit?: number
}

// ── preview stub (VITE_API_STUB=1 — no backend) ─────────────────────────────────
// When PREVIEW_STUB, every operation reads/writes a module-level mutable copy of the
// seed instead of fetching. Mutations rebuild totals via recomputeStubOrder() so the
// /order screen is fully interactive (+/-, split, discount, fire, pay, close). The
// real (non-stub) code paths below are untouched. NOT used in real builds.

let stubOrders: BackendOrderDto[] = structuredClone(SEED_ORDERS)

function stubFind(orderId: string): BackendOrderDto {
  const o = stubOrders.find((x) => x.id === orderId)
  if (!o) throw new Error(`ORDER_NOT_FOUND: ${orderId}`)
  return o
}
function stubReplace(next: BackendOrderDto): BackendOrderDto {
  const recomputed = recomputeStubOrder(next)
  stubOrders = stubOrders.map((x) => (x.id === recomputed.id ? recomputed : x))
  return structuredClone(recomputed)
}
let stubIdSeq = 1
const stubId = (prefix: string) => `${prefix}-stub-${Date.now().toString(36)}-${stubIdSeq++}`

function stubAddItem(orderId: string, item: AddItemInput): BackendOrderDto {
  const o = stubFind(orderId)
  const id = stubId('oi')
  const rate = Number(item.taxRate ?? '0.16') || 0
  const modifiers = (item.modifiers ?? []).map((m) => ({ id: stubId('m'), orderItemId: id, modifierId: m.modifierId, name: m.name, priceDeltaCents: m.priceDeltaCents }))
  const modDelta = modifiers.reduce((s, m) => s + m.priceDeltaCents, 0)
  const lineBase = (item.unitPriceCents + modDelta) * item.quantity - (item.discountCents ?? 0)
  const newItem: BackendOrderItemDto = {
    id, orderId, subAccountId: item.subAccountId ?? null, productId: item.productId,
    variantId: item.variantId ?? null, variantName: item.variantName ?? null, comboId: null,
    name: item.name, quantity: item.quantity, unitPriceCents: item.unitPriceCents,
    discountCents: item.discountCents ?? 0, taxRate: String(item.taxRate ?? '0.16'),
    taxCents: Math.round(lineBase * rate), totalCents: lineBase, status: 'PENDING',
    kitchenStationId: null, sentToKitchenAt: null, readyAt: null, deliveredAt: null,
    notes: item.notes ?? null, parentOrderItemId: null, isComboRoot: false,
    createdAt: new Date().toISOString(), modifiers,
  }
  return stubReplace({ ...o, items: [...o.items, newItem] })
}

// ── operations ──────────────────────────────────────────────────────────────────

export async function createOrder(input: CreateOrderInput): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = makeStubOrder({ type: input.type ?? 'DINE_IN', covers: input.covers ?? 1, tableSessionId: input.tableSessionId ?? null })
    stubOrders = [...stubOrders, o]
    return structuredClone(o)
  }
  // F8.1 / ADR-0009: ESCRITURA por el grafo (mutation tenant-guarded).
  const res = await gqlClient.mutation(PosCreateOrderMutation, { input: { branchId: input.branchId, openedByUserId: input.openedByUserId, ...(input.type ? { type: input.type } : {}), ...(input.tableSessionId ? { tableSessionId: input.tableSessionId } : {}), ...(input.covers !== undefined ? { covers: input.covers } : {}), ...(input.customerId ? { customerId: input.customerId } : {}) } }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.createOrder
  if (!o) throw new Error('CREATE_ORDER_FAILED')
  return o as unknown as BackendOrderDto
}

export async function getOrder(orderId: string, signal?: AbortSignal): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = stubOrders.find((x) => x.id === orderId) ?? stubOrders[0]
    if (!o) throw new Error(`ORDER_NOT_FOUND: ${orderId}`)
    return structuredClone(o)
  }
  // F4.1 / ADR-0009: lectura por el grafo (pos-orders, tenant del JWT). Escrituras REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(PosOrderQuery, { id: orderId }, ctx).toPromise()
  if (res.error) throw res.error
  const o = res.data?.order
  if (!o) throw new Error(`ORDER_NOT_FOUND: ${orderId}`)
  return o as unknown as BackendOrderDto
}

export async function listOrders(query: ListOrdersQuery & { tableSessionId?: string }, signal?: AbortSignal): Promise<OrderListResponse> {
  if (PREVIEW_STUB) {
    const items = stubOrders
      .filter((o) => (query.status ? o.status === query.status : true))
      .filter((o) => (query.tableSessionId ? o.tableSessionId === query.tableSessionId : true))
      .map((o) => structuredClone(o))
    return { items, total: items.length, page: query.page ?? 1, limit: query.limit ?? 50 }
  }
  // F4.1 / ADR-0009: lectura por el grafo (pos-orders, tenant del JWT). Escrituras REST.
  // El filtro por mesa se atiende con `findOpenOrderByTableSession` (query dedicada);
  // aquí no se envía (el grafo `orders` no lo expone — el tenant sale del JWT, no de query).
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const vars: { branchId?: string; status?: BackendOrderDto['status']; from?: string; to?: string; page?: number; limit?: number } = {}
  if (query.branchId) vars.branchId = query.branchId
  if (query.status) vars.status = query.status
  if (query.from) vars.from = query.from
  if (query.to) vars.to = query.to
  if (query.page) vars.page = query.page
  if (query.limit) vars.limit = query.limit
  const res = await gqlClient.query(PosOrdersQuery, vars, ctx).toPromise()
  if (res.error) throw res.error
  const pageRes = res.data?.orders
  return {
    items: (pageRes?.items ?? []) as unknown as BackendOrderDto[],
    total: pageRes?.total ?? 0,
    page: pageRes?.page ?? (query.page ?? 1),
    limit: pageRes?.limit ?? (query.limit ?? 20),
  }
}

/** Find an OPEN order tied to a given table session — used by the floor flows to
 *  recover the orderId when `PosTableSession.orderId` came back null (demo /
 *  legacy sessions). Returns the first OPEN order on that session, or null. */
export async function findOpenOrderByTableSession(
  tenantId: string,
  tableSessionId: string,
  signal?: AbortSignal,
): Promise<BackendOrderDto | null> {
  // F4.1 / ADR-0009: query dedicada del grafo (orderOpenByTableSession, tenant del JWT).
  if (PREVIEW_STUB) {
    const res = await listOrders({ tenantId, status: 'OPEN', tableSessionId, limit: 5 }, signal)
    return res.items.find((o) => o.tableSessionId === tableSessionId) ?? null
  }
  try {
    const ctx = signal ? { fetchOptions: { signal } } : undefined
    const res = await gqlClient.query(PosOrderOpenByTableSessionQuery, { tableSessionId }, ctx).toPromise()
    if (res.error) throw res.error
    const o = res.data?.orderOpenByTableSession
    return o ? (o as unknown as BackendOrderDto) : null
  } catch {
    return null
  }
}

export async function addOrderItem(orderId: string, item: AddItemInput): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) return stubAddItem(orderId, item)
  // Backend zod expects taxRate as `number`. The UI/order-store carry it as string
  // (the master catalog doesn't expose it; defaults to '0' or '0.16'). Coerce here
  // so any string ('0', '0.16', '') becomes a numeric rate.
  const body: AddItemInput = {
    ...item,
    taxRate: typeof item.taxRate === 'string' ? Number(item.taxRate) || 0 : (item.taxRate ?? 0),
  }
  // F8.1 / ADR-0009: ESCRITURA por el grafo (mutation tenant-guarded).
  const res = await gqlClient.mutation(PosAddOrderItemMutation, { orderId, input: { productId: body.productId, name: body.name, quantity: body.quantity, unitPriceCents: body.unitPriceCents, ...(body.taxRate !== undefined ? { taxRate: Number(body.taxRate) } : {}), ...(body.discountCents !== undefined ? { discountCents: body.discountCents } : {}), ...(body.subAccountId ? { subAccountId: body.subAccountId } : {}), ...(body.variantId ? { variantId: body.variantId } : {}), ...(body.variantName ? { variantName: body.variantName } : {}), ...(body.notes ? { notes: body.notes } : {}), ...(body.modifiers ? { modifiers: body.modifiers } : {}) } }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.addOrderItem
  if (!o) throw new Error('ADD_ITEM_FAILED')
  return o as unknown as BackendOrderDto
}

export async function updateOrderItem(orderId: string, itemId: string, patch: UpdateItemInput, managerAuthToken?: string): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    const items = o.items.map((it) => {
      if (it.id !== itemId) return it
      const quantity = patch.quantity ?? it.quantity
      const discountCents = patch.discountCents ?? it.discountCents
      const modDelta = it.modifiers.reduce((s, m) => s + m.priceDeltaCents, 0)
      const lineBase = (it.unitPriceCents + modDelta) * quantity - discountCents
      const rate = Number(it.taxRate) || 0
      return { ...it, quantity, discountCents, totalCents: lineBase, taxCents: Math.round(lineBase * rate), ...(patch.notes !== undefined ? { notes: patch.notes } : {}), ...(patch.status ? { status: patch.status } : {}) }
    })
    return stubReplace({ ...o, items })
  }
  // F8.1 / ADR-0009: ESCRITURA por el grafo (mutation tenant-guarded).
  const res = await gqlClient.mutation(PosUpdateOrderItemMutation, { orderId, itemId, input: { ...(patch.quantity !== undefined ? { quantity: patch.quantity } : {}), ...(patch.discountCents !== undefined ? { discountCents: patch.discountCents } : {}), ...(patch.notes !== undefined ? { notes: patch.notes } : {}), ...(patch.status ? { status: patch.status } : {}) }, ...(managerAuthToken ? { managerAuthToken } : {}) }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.updateOrderItem
  if (!o) throw new Error('UPDATE_ITEM_FAILED')
  return o as unknown as BackendOrderDto
}

export async function removeOrderItem(orderId: string, itemId: string, managerAuthToken?: string): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    return stubReplace({ ...o, items: o.items.filter((it) => it.id !== itemId) })
  }
  // F8.1 / ADR-0009: ESCRITURA por el grafo (mutation tenant-guarded).
  // TCK-0016: managerAuthToken (de manager-verify) requerido para barra/cajero.
  const res = await gqlClient.mutation(PosRemoveOrderItemMutation, { orderId, itemId, ...(managerAuthToken ? { managerAuthToken } : {}) }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.removeOrderItem
  if (!o) throw new Error('REMOVE_ITEM_FAILED')
  return o as unknown as BackendOrderDto
}

export async function fireOrder(orderId: string): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    const now = new Date().toISOString()
    return stubReplace({ ...o, items: o.items.map((it) => (it.status === 'PENDING' ? { ...it, status: 'SENT' as const, sentToKitchenAt: now } : it)) })
  }
  // F8.1 / ADR-0009: ESCRITURA por el grafo (mutation tenant-guarded).
  const res = await gqlClient.mutation(PosFireOrderMutation, { orderId }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.fireOrder
  if (!o) throw new Error('FIRE_FAILED')
  return o as unknown as BackendOrderDto
}

export async function applyOrderDiscount(orderId: string, input: ApplyOrderDiscountInput): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    const amount = Math.max(0, Math.round(input.discountCents))
    const meta = { reason: input.reason, appliedBy: input.appliedBy, ...(input.subAccountId ? { subAccountId: input.subAccountId } : {}) }
    if (input.subAccountId) {
      return stubReplace({
        ...o,
        discountMeta: meta,
        subAccounts: o.subAccounts.map((sa) => (sa.id === input.subAccountId ? { ...sa, discountCents: amount } : sa)),
      })
    }
    return stubReplace({ ...o, discountCents: amount, discountMeta: meta })
  }
  // F8.1 / ADR-0009: ESCRITURA por el grafo (mutation tenant-guarded).
  const res = await gqlClient.mutation(PosApplyOrderDiscountMutation, { orderId, input: { discountCents: input.discountCents, reason: input.reason, appliedBy: input.appliedBy } }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.applyOrderDiscount
  if (!o) throw new Error('DISCOUNT_FAILED')
  return o as unknown as BackendOrderDto
}

export async function setOrderTip(orderId: string, tipCents: number): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    return stubReplace({ ...o, tipCents: Math.max(0, Math.round(tipCents)) })
  }
  // F8.1 / ADR-0009: ESCRITURA por el grafo (mutation tenant-guarded).
  const res = await gqlClient.mutation(PosSetOrderTipMutation, { orderId, tipCents }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.setOrderTip
  if (!o) throw new Error('SET_TIP_FAILED')
  return o as unknown as BackendOrderDto
}

/** Record a payment against an order (partial / sub-account / mixed). Bumps paidCents +
 *  status on dhk8-pos-orders and persists a payment record (folio on `ticketFolio` once
 *  fully paid). `idempotencyKey` is sent both in the body and as the `Idempotency-Key`
 *  header so a retried tap doesn't double-charge. POST /v1/orders/:id/payments. */
export async function recordOrderPayment(orderId: string, input: RecordPaymentInput): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    return stubReplace({ ...o, paidCents: o.paidCents + Math.round(input.amountCents), ...(input.tipCents ? { tipCents: input.tipCents } : {}) })
  }
  // F8.1 / ADR-0009: ESCRITURA por el grafo (mutation tenant-guarded). El service
  // persiste PosPayment + actualiza la orden (mismo método que la ruta REST). F9.6:
  // idempotencyKey + cardLastFour van en el input del grafo (F9.11b: el schema los
  // acepta y el service persiste PosPayment.cardLastFour). cashGivenCents no va (el
  // POS deriva changeCents y manda eso).
  const gqlInput: { amountCents: number; method: string; capturedById: string; tipCents?: number; changeCents?: number; reference?: string; cardLastFour?: string; subAccountId?: string; idempotencyKey?: string } = {
    amountCents: input.amountCents, method: input.method, capturedById: input.capturedById,
  }
  if (input.tipCents !== undefined) gqlInput.tipCents = input.tipCents
  if (input.changeCents !== undefined) gqlInput.changeCents = input.changeCents
  if (input.reference !== undefined) gqlInput.reference = input.reference
  if (input.cardLastFour !== undefined) gqlInput.cardLastFour = input.cardLastFour
  if (input.subAccountId) gqlInput.subAccountId = input.subAccountId
  // F9.6: thread the idempotency key into the graph input (server dedupes a retried
  // tap -> a single payment). Back-compat: omitted when the caller has no key.
  if (input.idempotencyKey) gqlInput.idempotencyKey = input.idempotencyKey
  const res = await gqlClient.mutation(PosRecordOrderPaymentMutation, { orderId, input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.recordOrderPayment
  if (!o) throw new Error('RECORD_PAYMENT_FAILED')
  return o as unknown as BackendOrderDto
}

/** List the persisted payment records on an order (for the receipt / refund screen).
 *  GET /v1/orders/:id/payments. */
export async function listOrderPayments(orderId: string, signal?: AbortSignal): Promise<BackendOrderPaymentDto[]> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    // The stub doesn't persist payment records; synthesise one from paidCents so callers
    // (e.g. the refund screen) have something to act on.
    if (o.paidCents <= 0) return []
    return [{
      id: `pay-stub-${o.id}`, orderId: o.id, subAccountId: null, method: 'CASH',
      amountCents: o.paidCents, tipCents: o.tipCents, changeCents: 0, cardLastFour: null,
      reference: null, status: 'CAPTURED', refundedCents: 0, capturedById: o.openedByUserId ?? 'u-stub',
      capturedAt: o.closedAt ?? o.openedAt ?? new Date().toISOString(), refundedAt: null,
    }]
  }
  // F4.2 / ADR-0009: lectura por el grafo (pos-orders orderPayments, tenant del JWT).
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(PosOrderPaymentsQuery, { orderId }, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.orderPayments ?? []) as unknown as BackendOrderPaymentDto[]
}

/** Refund (full or partial) a recorded payment. Decrements paidCents and may reopen the
 *  order. Manager-authorised — gate the caller with `requestManagerPin('ticket.refund')`.
 *  POST /v1/orders/:id/payments/:paymentId/refund. */
export async function refundOrderPayment(orderId: string, paymentId: string, input: RefundPaymentInput): Promise<RefundPaymentResult> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    const refund = Math.max(0, Math.round(input.amountCents ?? o.paidCents))
    const order = stubReplace({ ...o, paidCents: Math.max(0, o.paidCents - refund) })
    const fully = refund >= (input.amountCents ?? o.paidCents) || order.paidCents <= 0
    return {
      payment: {
        id: paymentId, orderId, subAccountId: null, method: 'CASH', amountCents: o.paidCents,
        tipCents: o.tipCents, changeCents: 0, cardLastFour: null, reference: null,
        status: fully ? 'REFUNDED' : 'PARTIALLY_REFUNDED', refundedCents: refund,
        capturedById: o.openedByUserId ?? 'u-stub', capturedAt: o.closedAt ?? o.openedAt ?? new Date().toISOString(),
        refundedAt: new Date().toISOString(),
      },
      order,
    }
  }
  return fetchData<RefundPaymentResult>(
    `${ORDERS_BASE}/${encodeURIComponent(orderId)}/payments/${encodeURIComponent(paymentId)}/refund`,
    { method: 'POST', body: input },
  )
}

/** Order-level refund (F3) — manager-PIN gated. Creates a PosRefund row + emits
 *  `dhk8.pos.refund.created`, decrements paidCents on the order. The caller MUST
 *  pre-verify the manager PIN against dhk8-platform-auth's /v1/auth/manager-verify
 *  and forward proof in `managerPinPasswordHash`. POST /v1/orders/:orderId/refund. */
export async function refundOrder(orderId: string, input: RefundOrderInput): Promise<RefundOrderResult> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    const refund = Math.max(0, Math.round(input.amountCents))
    const order = stubReplace({ ...o, paidCents: Math.max(0, o.paidCents - refund) })
    return {
      refund: {
        id: `ref-stub-${Date.now()}`, tenantId: order.tenantId, branchId: order.branchId, orderId,
        paymentId: null, amountCents: refund, reason: input.reason, authorizedById: input.managerUserId,
        status: 'DONE', cfdiCancelled: false, createdAt: new Date().toISOString(),
      },
      order,
    }
  }
  // F9.10 / ADR-0008 + ADR-0019: ESCRITURA (money-path) por el grafo. El resolver
  // gatea con el permiso `pos-orders:order:refund@*` del JWT; cuando el valor es
  // 'manager_pin_required' exige `managerPinPasswordHash` (la misma prueba de
  // manager-auth que ya verifica el flujo del PIN-gate). Sin tenantId (va en el JWT).
  const res = await gqlClient.mutation(PosRefundOrderMutation, {
    orderId,
    input: {
      amountCents: input.amountCents,
      reason: input.reason,
      managerUserId: input.managerUserId,
      managerPinPasswordHash: input.managerPinPasswordHash,
      // F9.6: dedupe a retried/replayed refund into a single PosRefund. Optional (back-compat).
      ...(input.idempotencyKey ? { idempotencyKey: input.idempotencyKey } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.refundOrder
  if (!r) throw new Error('REFUND_FAILED')
  return r as unknown as RefundOrderResult
}

export async function cancelOrder(orderId: string, reason: string, managerAuthToken?: string): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    return stubReplace({ ...o, status: 'CANCELLED', cancelledAt: new Date().toISOString(), cancelReason: reason, items: o.items.map((it) => ({ ...it, status: 'CANCELLED' as const })) })
  }
  // F8.1 / ADR-0009: ESCRITURA por el grafo (mutation tenant-guarded).
  // TCK-0016: managerAuthToken requerido para barra/cajero.
  const res = await gqlClient.mutation(PosCancelOrderMutation, { orderId, reason, ...(managerAuthToken ? { managerAuthToken } : {}) }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.cancelOrder
  if (!o) throw new Error('CANCEL_FAILED')
  return o as unknown as BackendOrderDto
}

// Lane #172 — Reopen: gerente revierte CLOSED → OPEN. La orden vuelve a ser
// editable (agregar/quitar items, ajustar pagos). Los pagos existentes NO se
// anulan automáticamente; el cajero decide refund o ajuste.
export async function reopenOrder(orderId: string, reason: string, managerAuthToken?: string): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    return stubReplace({ ...o, status: 'OPEN', closedAt: null })
  }
  // F8.1 / ADR-0009: ESCRITURA por el grafo (mutation tenant-guarded).
  // TCK-0016: managerAuthToken requerido para barra/cajero.
  const res = await gqlClient.mutation(PosReopenOrderMutation, { orderId, reason, ...(managerAuthToken ? { managerAuthToken } : {}) }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.reopenOrder
  if (!o) throw new Error('REOPEN_FAILED')
  return o as unknown as BackendOrderDto
}

export async function createOrderSubAccount(orderId: string, label: string): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    const sa: BackendOrderSubAccountDto = { id: stubId('sa'), orderId, label, discountCents: 0, tipCents: 0, notes: null, createdAt: new Date().toISOString(), closedAt: null }
    return stubReplace({ ...o, subAccounts: [...o.subAccounts, sa] })
  }
  // F8.1 / ADR-0009: ESCRITURA por el grafo (mutation tenant-guarded).
  const res = await gqlClient.mutation(PosCreateOrderSubAccountMutation, { orderId, label }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.createOrderSubAccount
  if (!o) throw new Error('CREATE_SUBACCT_FAILED')
  return o as unknown as BackendOrderDto
}

// Lane #166 — rename de sub-cuenta existente. Antes el SplitDialog dejaba editar
// labels pero nunca llegaban al backend → cambios silenciosamente perdidos.
export async function updateOrderSubAccount(orderId: string, subAccountId: string, label: string): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    return stubReplace({
      ...o,
      subAccounts: o.subAccounts.map((sa) => (sa.id === subAccountId ? { ...sa, label } : sa)),
    })
  }
  // F8.1 / ADR-0009: escritura por el grafo (mutation tenant-guard anti-IDOR).
  const res = await gqlClient.mutation(PosUpdateOrderSubAccountMutation, { orderId, subAccountId, label }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.updateOrderSubAccount
  if (!o) throw new Error(`NOT_FOUND: order ${orderId}`)
  return o as unknown as BackendOrderDto
}

// TODO backend: dhk8-pos-orders does not publish this route yet. The expected shape is
// PATCH /v1/orders/:id/items/:itemId/sub-account { subAccountId } → updated OrderDto.
// When it lands, reconcile against services/dhk8-pos-orders/src/contract.ts. Until then
// the SplitDialog "por ítems" flow falls back to creating the sub-accounts only (the
// per-item move is best-effort and swallowed in order-store.splitOrder).
// `subAccountId === null` moves the item back to the "Principal" (unassigned) bucket.
export async function assignOrderItemSubAccount(orderId: string, itemId: string, subAccountId: string | null): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    return stubReplace({ ...o, items: o.items.map((it) => (it.id === itemId ? { ...it, subAccountId: subAccountId ?? null } : it)) })
  }
  return fetchData<BackendOrderDto>(
    `${ORDERS_BASE}/${encodeURIComponent(orderId)}/items/${encodeURIComponent(itemId)}/sub-account`,
    { method: 'PATCH', body: { subAccountId } },
  )
}

// ── Super-set guest — aceptación manual (Fase 1.6) ──────────────────────────
// Vía REST (no grafo): el DTO REST ya expone chosenPaymentMethod/fireHoldUntil,
// y el schema.graphql local aún no trae accept/reject. Escrituras siempre REST
// (ADR-0009); la lista de pendientes también, para traer los campos nuevos.

/** Órdenes de comensal esperando autorización del staff (status PENDING_ACCEPTANCE). */
export async function listPendingAcceptanceOrders(tenantId: string, branchId?: string): Promise<BackendOrderDto[]> {
  if (PREVIEW_STUB) {
    return stubOrders.filter((o) => o.status === 'PENDING_ACCEPTANCE').map((o) => structuredClone(o))
  }
  const qs = new URLSearchParams({ tenantId, status: 'PENDING_ACCEPTANCE', limit: '100' })
  if (branchId) qs.set('branchId', branchId)
  const res = await fetchData<{ items: BackendOrderDto[]; total: number }>(
    `${ORDERS_BASE}?${qs.toString()}`,
    { method: 'GET' },
  )
  return res.items ?? []
}

/** Acepta una orden guest → OPEN + ventana de gracia (fireHoldUntil). Staff. */
export async function acceptOrder(orderId: string, acceptedByUserId: string, graceSeconds?: number, fireWithoutPayment?: boolean): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    return stubReplace({ ...o, status: 'OPEN', acceptedByUserId, acceptedAt: new Date().toISOString() })
  }
  return fetchData<BackendOrderDto>(
    `${ORDERS_BASE}/${encodeURIComponent(orderId)}/accept`,
    { method: 'POST', body: { acceptedByUserId, ...(graceSeconds !== undefined ? { graceSeconds } : {}), ...(fireWithoutPayment ? { fireWithoutPayment: true } : {}) } },
  )
}

/** Gate de métodos de pago del tenant (staff). Fuente: dhk8-payments-gateway
 *  (el MISMO que edita admin › Pagos). Se usa para saber si el negocio permite
 *  "Aceptar sin pago" (cobrar al recoger). */
export interface TenantPaymentMethods {
  cashEnabled: boolean
  terminalEnabled: boolean
  onlineEnabled: boolean
  fireOnPrepay: boolean
  allowAcceptWithoutPayment: boolean
}

export async function getTenantPaymentMethods(): Promise<TenantPaymentMethods> {
  if (PREVIEW_STUB) {
    return { cashEnabled: true, terminalEnabled: false, onlineEnabled: false, fireOnPrepay: true, allowAcceptWithoutPayment: false }
  }
  return fetchData<TenantPaymentMethods>(svcUrl('dhk8-payments-gateway', '/v1/payments/methods'))
}

/** Rechaza una orden guest → REJECTED (razón obligatoria). Staff. */
export async function rejectOrder(orderId: string, rejectedByUserId: string, reason: string): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    const o = stubFind(orderId)
    return stubReplace({ ...o, status: 'REJECTED', rejectedByUserId, rejectReason: reason, rejectedAt: new Date().toISOString() })
  }
  return fetchData<BackendOrderDto>(
    `${ORDERS_BASE}/${encodeURIComponent(orderId)}/reject`,
    { method: 'POST', body: { rejectedByUserId, reason } },
  )
}

// ── Aceptación por RONDA (tandas remotas del comensal) ──────────────────────
// El comensal somete lo que pidió sobre una cuenta ya abierta (submit-round,
// desde guest); aquí el mesero lo autoriza. accept → dispara a cocina (nace su
// ticket); reject → cancela sus ítems. REST como accept/reject de orden; el
// backend exige tenantId en el body (verificación cross-tenant de la ronda).
const ROUNDS_BASE = svcUrl('dhk8-pos-orders', '/v1/rounds')

export async function acceptRound(roundId: string, tenantId: string, acceptedByUserId: string): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) return stubOrders[0]!
  return fetchData<BackendOrderDto>(
    `${ROUNDS_BASE}/${encodeURIComponent(roundId)}/accept`,
    { method: 'POST', body: { tenantId, acceptedByUserId } },
  )
}

export async function rejectRound(roundId: string, tenantId: string, rejectedByUserId: string, reason: string): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) return stubOrders[0]!
  return fetchData<BackendOrderDto>(
    `${ROUNDS_BASE}/${encodeURIComponent(roundId)}/reject`,
    { method: 'POST', body: { tenantId, rejectedByUserId, reason } },
  )
}

/** Órdenes ACTIVAS con al menos una ronda PENDIENTE de aceptación (tandas que
 *  el comensal sometió sobre su cuenta y el mesero aún no autoriza). El grafo
 *  `orders` ya trae `pendingRounds`; filtramos las que tengan ≥1. */
export async function listOrdersWithPendingRounds(tenantId: string, branchId?: string): Promise<BackendOrderDto[]> {
  if (PREVIEW_STUB) return []
  const res = await listOrders({ tenantId, ...(branchId ? { branchId } : {}), limit: 100 })
  return res.items.filter((o) => (o.pendingRounds?.length ?? 0) > 0)
}

// ── Entrega validada por código de recolección ──────────────────────────────
// El comensal llega con su código (pantalla/captura/PDF). El staff lo teclea, ve
// DE QUIÉN es el pedido y qué lleva, y sólo entonces lo entrega.

/** Orden + nombre del comensal (viene de la GuestSession; null si la levantó el staff). */
export type PickupLookupResult = BackendOrderDto & { customerName: string | null }

/** Busca el pedido por su código de recolección. 404 → el código no existe. */
export async function lookupOrderByPickupCode(tenantId: string, code: string): Promise<PickupLookupResult> {
  const qs = new URLSearchParams({ tenantId, code })
  return fetchData<PickupLookupResult>(`${ORDERS_BASE}/by-pickup-code?${qs.toString()}`, { method: 'GET' })
}

/** Marca el pedido como entregado. El backend rechaza la doble entrega. */
export async function deliverOrder(orderId: string, tenantId: string, deliveredByUserId: string): Promise<BackendOrderDto> {
  return fetchData<BackendOrderDto>(
    `${ORDERS_BASE}/${encodeURIComponent(orderId)}/deliver`,
    { method: 'POST', body: { tenantId, deliveredByUserId } },
  )
}

// ── Lane #165 — mover orden a otra mesa ─────────────────────────────────────
// Mesero libre + auditoría completa en outbox (decisión del user). Razones
// enum coinciden con el backend (schema.ts MoveOrderReasonSchema).

export type MoveOrderReason =
  | 'cleaning_required'
  | 'noise'
  | 'climate_ac'
  | 'customer_preference'
  | 'wants_couch'
  | 'wants_table'
  | 'reservation_arrived'
  | 'other'

export interface MoveOrderInput {
  toTableId: string
  reason: MoveOrderReason
  notes?: string
  movedBy: string
}

export async function moveOrder(orderId: string, input: MoveOrderInput): Promise<BackendOrderDto> {
  if (PREVIEW_STUB) {
    // En stub no movemos físicamente — devolvemos la orden tal cual.
    const o = stubFind(orderId)
    return structuredClone(o)
  }
  // F8.1 / ADR-0009: escritura por el grafo (mutation tenant-guard anti-IDOR).
  const res = await gqlClient.mutation(PosMoveOrderMutation, {
    orderId,
    input: { toTableId: input.toTableId, reason: input.reason, notes: input.notes ?? null, movedBy: input.movedBy },
  }).toPromise()
  if (res.error) throw res.error
  const o = res.data?.moveOrder
  if (!o) throw new Error(`NOT_FOUND: order ${orderId}`)
  return o as unknown as BackendOrderDto
}

// ── training-mode / test reset ──────────────────────────────────────────────────
// Re-clone the pristine seeds into the module-level stub state. Called by the
// TrainingDataProvider on entering / exiting training mode (so a trainee's edits
// vanish) and by tests.
export function __resetOrdersStub(): void {
  stubOrders = structuredClone(SEED_ORDERS)
  stubIdSeq = 1
}

// ── offline outbox replay (GraphQL) ─────────────────────────────────────────────
// The write path migrated REST->GraphQL (F8.1 / ADR-0009). When a write fails while
// offline, order-store parks a graph-op descriptor in the outbox (lib/offline-queue.ts)
// and replays it on reconnect through this dispatcher, which calls the SAME graph
// client fns the online path uses. We re-cast the saved `vars` (which round-tripped
// through IndexedDB as plain JSON) back to their typed shapes here.
// F9.6: idempotencyKey rides on the OutboxEntry AND is forwarded to the money-path graph
// mutations (recordOrderPayment / refundOrder) so a replayed entry dedupes server-side
// into a single payment/refund. Item ops (add/update/remove/fire) don't take a graph-level
// key; the FIFO-stop-on-failure flush keeps their replay window small (an entry is removed
// only after its mutation resolves).
export type ReplayGraphOp =
  | { name: 'addOrderItem'; orderId: string; vars: { input: AddItemInput } }
  | { name: 'updateOrderItem'; orderId: string; vars: { itemId: string; input: UpdateItemInput } }
  | { name: 'removeOrderItem'; orderId: string; vars: { itemId: string } }
  | { name: 'fireOrder'; orderId: string; vars: Record<string, never> }
  | { name: 'recordOrderPayment'; orderId: string; vars: { input: RecordPaymentInput } }
  | { name: 'refundOrder'; orderId: string; vars: { input: RefundOrderInput } }

export async function replayGraphOp(op: { name: string; orderId: string; vars: Record<string, unknown> }, idempotencyKey?: string): Promise<BackendOrderDto> {
  switch (op.name) {
    case 'addOrderItem': {
      const input = op.vars.input as AddItemInput
      return addOrderItem(op.orderId, input)
    }
    case 'updateOrderItem': {
      const itemId = op.vars.itemId as string
      const input = (op.vars.input ?? {}) as UpdateItemInput
      return updateOrderItem(op.orderId, itemId, input)
    }
    case 'removeOrderItem': {
      const itemId = op.vars.itemId as string
      return removeOrderItem(op.orderId, itemId)
    }
    case 'fireOrder':
      return fireOrder(op.orderId)
    case 'recordOrderPayment': {
      // F9.6: forward the outbox entry's idempotency key so a replayed tap dedupes
      // into a single payment server-side. input.idempotencyKey (if persisted) wins.
      const input = op.vars.input as RecordPaymentInput
      return recordOrderPayment(op.orderId, { ...input, idempotencyKey: input.idempotencyKey ?? idempotencyKey })
    }
    case 'refundOrder': {
      const input = op.vars.input as RefundOrderInput
      const { order } = await refundOrder(op.orderId, { ...input, idempotencyKey: input.idempotencyKey ?? idempotencyKey })
      return order
    }
    default:
      throw new Error(`UNKNOWN_OUTBOX_OP: ${op.name}`)
  }
}
